import { useState, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { useQueries, useQueryClient } from "@tanstack/react-query";

import { payrollAPI, companyAPI } from "../services/api";
import { useCurrencyFormatter } from "../hooks/usePayrollFormatters";
import { generateTunisiaPayslipPdf } from "../utils/tunisiaPayslipPdf";
import { isPeriodLocked } from "../components/payroll/payrollExportsHelpers";
import CalculationProgress from "../components/payroll/CalculationProgress";
import PayrollNotifications from "../components/payroll/PayrollNotifications";
import PayrollKPIDashboard from "../components/payroll/PayrollKPIDashboard";
import ApprovalWorkflowInterface from "../components/payroll/ApprovalWorkflowInterface";
import PayrollEntryDetails from "../components/payroll/PayrollEntryDetails";
import PayslipPDFModal from "../components/payroll/PayslipPDFModal";
import PayrollHeader from "../components/payroll/PayrollHeader";
import PayrollFiltersCard from "../components/payroll/PayrollFiltersCard";
import PayrollEntriesCard from "../components/payroll/PayrollEntriesCard";
import PayrollPeriodSummaryCards from "../components/payroll/PayrollPeriodSummaryCards";
import PayrollAnalyticsDashboard from "../components/payroll/PayrollAnalyticsDashboard";

const PayrollCalculations = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [selectedPeriodId, setSelectedPeriodId] = useState("");
  const [calculating, setCalculating] = useState(false);
  const [calculationProgress, setCalculationProgress] = useState(0);

  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [departmentFilter, setDepartmentFilter] = useState("all");

  const [isPdfModalOpen, setIsPdfModalOpen] = useState(false);
  const [selectedEntryForPdf, setSelectedEntryForPdf] = useState(null);

  const [selectedEntries, setSelectedEntries] = useState([]);
  const [bulkActionLoading, setBulkActionLoading] = useState(false);

  const [selectedEntryForDetails, setSelectedEntryForDetails] = useState(null);
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);

  const [dashboardView, setDashboardView] = useState("overview");
  const [showAllEntries, setShowAllEntries] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [realTimeData, setRealTimeData] = useState({
    lastUpdate: new Date(),
    processingStatus: "idle",
    pendingApprovals: 0,
  });

  // useQueries must come before useMemo hooks that reference derived data (avoids TDZ crash)
  const [periodsQ, entriesQ, summaryQ, companyQ] = useQueries({
    queries: [
      {
        queryKey: ["payroll-periods"],
        queryFn: () => payrollAPI.getPayrollPeriods({ page: 1, limit: 20 }).then((d) => d.items || []),
      },
      {
        queryKey: ["payroll-entries", selectedPeriodId],
        queryFn: () => payrollAPI.getPayrollEntries(selectedPeriodId, { page: 1, limit: 100 }).then((d) => d.items || []),
        enabled: !!selectedPeriodId,
      },
      {
        queryKey: ["payroll-summary", selectedPeriodId],
        queryFn: () => payrollAPI.getPayrollSummary(selectedPeriodId),
        enabled: !!selectedPeriodId,
      },
      {
        queryKey: ["company-info-payroll"],
        queryFn: () => companyAPI.getCompanyInfo().then(d => d?.company ?? d).catch(() => null),
        staleTime: 5 * 60 * 1000,
      },
    ],
  });

  const periods = periodsQ.data ?? [];
  const entries = entriesQ.data ?? [];
  const summary = summaryQ.data ?? null;
  const _company = companyQ.data;
  const companyInfo = _company ? {
    name: _company.trade_name || _company.legal_name || "",
    legalName: _company.legal_name || "",
    address: _company.address_line1 || "",
    registration: _company.siret || _company.siren || _company.vat_number || "",
    taxIdentifier: _company.vat_number || _company.siret || _company.siren || "",
    cnssNumber: _company.cnss_number || "",
    activity: _company.ape_code || "",
  } : null;
  const loading = entriesQ.isPending && !!selectedPeriodId;

  const filteredEntries = useMemo(() => {
    let filtered = entries;
    if (searchTerm.trim()) {
      const search = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (entry) =>
          entry.employee_full_name?.toLowerCase().includes(search) ||
          entry.employee_registration_number?.toLowerCase().includes(search) ||
          entry.employee_department?.toLowerCase().includes(search),
      );
    }
    if (statusFilter !== "all") {
      filtered = filtered.filter((entry) => {
        switch (statusFilter) {
          case "approved": return entry.is_approved;
          case "pending": return !entry.is_approved;
          case "errors": return entry.has_errors;
          default: return true;
        }
      });
    }
    if (departmentFilter !== "all") {
      filtered = filtered.filter((entry) => entry.employee_department === departmentFilter);
    }
    return filtered;
  }, [entries, searchTerm, statusFilter, departmentFilter]);

  const departments = useMemo(() => {
    const depts = [...new Set(entries.map((e) => e.employee_department).filter(Boolean))];
    return depts.sort();
  }, [entries]);

  const handleSelectAll = (checked) => {
    setSelectedEntries(checked ? filteredEntries.map((entry) => entry.id) : []);
  };

  const handleSelectEntry = (entryId, checked) => {
    setSelectedEntries(checked
      ? [...selectedEntries, entryId]
      : selectedEntries.filter((id) => id !== entryId)
    );
  };

  const handleShowEntryDetails = (entry) => {
    setSelectedEntryForDetails(entry);
    setIsDetailsModalOpen(true);
  };

  const handleCloseEntryDetails = () => {
    setIsDetailsModalOpen(false);
    setSelectedEntryForDetails(null);
  };

  const handleGeneratePDF = (entry) => {
    setSelectedEntryForPdf(entry);
    setIsPdfModalOpen(true);
    addNotification("info", t("payrollCalc.pdfGenTitle"), t("payrollCalc.pdfGenMsg", { name: entry.employee_full_name }));
  };

  const handleClosePdfModal = () => {
    setIsPdfModalOpen(false);
    setSelectedEntryForPdf(null);
  };

  const handleBulkPDFGeneration = async () => {
    try {
      setBulkActionLoading(true);
      const entriesToProcess =
        selectedEntries.length > 0 ? filteredEntries.filter((e) => selectedEntries.includes(e.id)) : filteredEntries;
      if (entriesToProcess.length === 0) {
        toast.error(t("payrollCalc.pdfNoEntries"));
        return;
      }
      addNotification("info", t("payrollCalc.bulkPdfTitle"), t("payrollCalc.bulkPdfMsg", { count: entriesToProcess.length }));

      entriesToProcess.forEach((entry, index) => {
        if (index % 5 === 0) {
          const progress = Math.round(((index + 1) / entriesToProcess.length) * 100);
          addNotification("info", t("payrollCalc.progressTitle"), t("payrollCalc.progressMsg", { progress, current: index + 1, total: entriesToProcess.length }));
        }
      });

      const pdf = generateTunisiaPayslipPdf({
        entries: entriesToProcess,
        companyInfo,
        selectedPeriod,
        formatCurrency,
        t,
      });
      const filename = `bulletins-paie-lot-${selectedPeriod?.start_date || new Date().toISOString().split("T")[0]}.pdf`;
      pdf.save(filename);
      if (entriesToProcess.length > 1) {
        addNotification("info", t("payrollCalc.bulkPdfSingleFileTitle"), t("payrollCalc.bulkPdfSingleFileMsg", { count: entriesToProcess.length }));
      }
      addNotification("success", t("payrollCalc.bulkPdfDoneTitle"), t("payrollCalc.bulkPdfDoneMsg", { count: entriesToProcess.length }));
      setSelectedEntries([]);
    } catch (error) {
      addNotification("error", t("payrollCalc.pdfErrorTitle"), t("payrollCalc.pdfErrorMsg"));
    } finally {
      setBulkActionLoading(false);
    }
  };

  const handleDismissNotification = (notificationId) => {
    setNotifications((prev) => prev.filter((n) => n.id !== notificationId));
  };

  const addNotification = (type, title, message) => {
    const newNotif = { id: Date.now(), type, title, message, timestamp: new Date().toLocaleTimeString() };
    setNotifications((prev) => [newNotif, ...prev.slice(0, 4)]);
  };

  const handleWorkflowBulkAction = async (action) => {
    try {
      setBulkActionLoading(true);
      const pendingEntries = filteredEntries.filter((e) => !e.is_approved);
      if (action === "approve") {
        addNotification("info", t("payrollCalc.approveProgressTitle"), t("payrollCalc.approveProgressMsg", { count: pendingEntries.length }));
        for (const entry of pendingEntries) {
          await payrollAPI.approvePayrollEntry(entry.id);
        }
        addNotification("success", t("payrollCalc.approveDoneTitle"), t("payrollCalc.approveDoneMsg", { count: pendingEntries.length }));
        queryClient.invalidateQueries({ queryKey: ["payroll-entries", selectedPeriodId] });
      } else if (action === "review") {
        addNotification("info", t("payrollCalc.reviewModeTitle"), t("payrollCalc.reviewModeMsg"));
      }
    } catch (error) {
      addNotification("error", t("payrollCalc.workflowErrorTitle"), t("payrollCalc.workflowErrorMsg"));
    } finally {
      setBulkActionLoading(false);
    }
  };

  useEffect(() => {
    const interval = setInterval(() => {
      setRealTimeData((prev) => ({
        ...prev,
        lastUpdate: new Date(),
        pendingApprovals: filteredEntries.filter((e) => !e.is_approved).length,
      }));
    }, 30000);
    return () => clearInterval(interval);
  }, [filteredEntries]);

  const handleBulkApprove = async (entryIds = selectedEntries) => {
    const idsToApprove = Array.isArray(entryIds) ? entryIds : selectedEntries;
    try {
      setBulkActionLoading(true);
      toast.info(t("payrollCalc.approveBulkInfo", { count: idsToApprove.length }));
      for (const entryId of idsToApprove) {
        await payrollAPI.approvePayrollEntry(entryId);
      }
      toast.success(t("payrollCalc.approveBulkSuccess", { count: idsToApprove.length }));
      setSelectedEntries([]);
      queryClient.invalidateQueries({ queryKey: ["payroll-entries", selectedPeriodId] });
    } catch (error) {
      toast.error(t("payrollCalc.approveBulkError"));
    } finally {
      setBulkActionLoading(false);
    }
  };

  const handleBulkReject = async (entryIds = selectedEntries) => {
    const idsToReject = Array.isArray(entryIds) ? entryIds : selectedEntries;
    try {
      setBulkActionLoading(true);
      toast.info(t("payrollCalc.rejectBulkInfo", { count: idsToReject.length }));
      for (const entryId of idsToReject) {
        await payrollAPI.rejectPayrollEntry(entryId);
      }
      toast.success(t("payrollCalc.rejectBulkSuccess", { count: idsToReject.length }));
      setSelectedEntries([]);
      queryClient.invalidateQueries({ queryKey: ["payroll-entries", selectedPeriodId] });
    } catch (error) {
      toast.error(t("payrollCalc.rejectBulkError"));
    } finally {
      setBulkActionLoading(false);
    }
  };

  const handleBulkExport = async (entryIds = []) => {
    try {
      setBulkActionLoading(true);
      const idsToExport =
        entryIds.length > 0
          ? entryIds
          : selectedEntries.length > 0
            ? selectedEntries
            : filteredEntries.map((e) => e.id);
      toast.info(t("payrollCalc.exportInfo", { count: idsToExport.length }));
      const blob = await payrollAPI.exportPayslips(selectedPeriodId, idsToExport);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `bulletins-paie-${idsToExport.length === 1 ? "individual" : "selection"}-${new Date().toISOString().split("T")[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success(t("payrollCalc.exportCsvSuccess"));
    } catch (error) {
      toast.error(t("payrollCalc.exportError"));
    } finally {
      setBulkActionLoading(false);
    }
  };

  const handleExportExcel = async (entryIds = []) => {
    try {
      setBulkActionLoading(true);
      const idsToExport =
        entryIds.length > 0
          ? entryIds
          : selectedEntries.length > 0
            ? selectedEntries
            : filteredEntries.map((e) => e.id);
      toast.info(t("payrollCalc.exportExcelInfo", { count: idsToExport.length }));
      const blob = await payrollAPI.exportPayrollExcel(selectedPeriodId, idsToExport);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `paie-${selectedPeriod?.start_date}-${new Date().toISOString().split("T")[0]}.xlsx`;
      document.body.appendChild(a);
      a.click();
      URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success(t("payrollCalc.exportExcelSuccess"));
    } catch (error) {
      toast.error(t("payrollCalc.exportExcelError"));
    } finally {
      setBulkActionLoading(false);
    }
  };

  useEffect(() => {
    setSelectedEntries([]);
    setShowAllEntries(false);
  }, [searchTerm, statusFilter, departmentFilter, selectedPeriodId]);

  useEffect(() => {
    if (periods.length > 0 && !selectedPeriodId) {
      setSelectedPeriodId(periods[0].id.toString());
    }
  }, [periods]);

  const invalidatePeriodData = () => {
    queryClient.invalidateQueries({ queryKey: ["payroll-entries", selectedPeriodId] });
    queryClient.invalidateQueries({ queryKey: ["payroll-summary", selectedPeriodId] });
    queryClient.invalidateQueries({ queryKey: ["payroll-periods"] });
  };

  const handleCalculatePeriod = async () => {
    if (!selectedPeriodId) {
      toast.error(t("payrollCalc.selectPeriod"));
      return;
    }
    const periodToCalc = periods.find((p) => p.id.toString() === selectedPeriodId);
    if (isPeriodLocked(periodToCalc)) {
      toast.error(t("payrollCalc.periodClosed"));
      return;
    }
    try {
      setCalculating(true);
      setCalculationProgress(0);
      toast.info(t("payrollCalc.calcStart"));

      const response = await payrollAPI.calculatePayrollPeriod(selectedPeriodId, {
        recalculate: true,
        period_id: selectedPeriodId,
      });

      let result;

      if (response.task_id && response.status === "pending") {
        // Async path — poll until complete
        const taskId = response.task_id;
        let elapsed = 0;
        while (true) {
          await new Promise((r) => setTimeout(r, 2000));
          elapsed += 2;
          // Smooth progress: ramp from 5% to 90% over ~60s, then hold
          setCalculationProgress(Math.min(5 + (elapsed / 60) * 85, 90));

          const poll = await payrollAPI.pollPayrollTask(taskId);
          if (poll.status === "completed") {
            result = poll.result;
            break;
          }
          if (poll.status === "failed") {
            throw new Error(poll.error || t("payrollCalc.asyncFail"));
          }
          // "pending" or "running" — keep polling
        }
      } else {
        // Sync fallback (no Celery) — result is already the full response
        result = response;
      }

      setCalculationProgress(100);
      if (result.total_errors > 0) {
        toast.warning(t("payrollCalc.calcWithErrors", { errors: result.total_errors, processed: result.total_processed }), { duration: 5000 });
      } else {
        toast.success(t("payrollCalc.calcSuccess", { processed: result.total_processed }), { duration: 4000 });
      }
      toast.info(t("payrollCalc.updatingData"));
      invalidatePeriodData();
    } catch (error) {
      toast.error(`${t("payrollCalc.calcError")}${error?.message ? ` : ${error.message}` : ""}`, { duration: 4000 });
      setCalculationProgress(0);
    } finally {
      setCalculating(false);
      setTimeout(() => setCalculationProgress(0), 3000);
    }
  };

  const selectedPeriod = periods.find((p) => p.id.toString() === selectedPeriodId);
  const periodClosed = isPeriodLocked(selectedPeriod);
  const formatCurrency = useCurrencyFormatter();

  return (
    <div className="space-y-6 relative">
      <PayrollNotifications notifications={notifications} onDismiss={handleDismissNotification} />

      <PayrollHeader
        realTimeData={realTimeData}
        periods={periods}
        selectedPeriodId={selectedPeriodId}
        setSelectedPeriodId={setSelectedPeriodId}
        calculating={calculating}
        handleCalculatePeriod={handleCalculatePeriod}
        periodClosed={periodClosed}
        dashboardView={dashboardView}
        setDashboardView={setDashboardView}
      />

      <CalculationProgress progress={calculationProgress} calculating={calculating} />

      {selectedPeriod && (
        <>
          {dashboardView === "overview" && (
            <div className="space-y-6">
              <PayrollKPIDashboard summary={summary} entries={filteredEntries} formatCurrency={formatCurrency} />
              <PayrollPeriodSummaryCards
                selectedPeriod={selectedPeriod}
                summary={summary}
                formatCurrency={formatCurrency}
              />
            </div>
          )}

          {dashboardView === "analytics" && (
            <PayrollAnalyticsDashboard filteredEntries={filteredEntries} />
          )}

          {dashboardView === "workflow" && (
            <div className="space-y-6">
              <ApprovalWorkflowInterface
                entries={filteredEntries}
                onBulkAction={handleWorkflowBulkAction}
                bulkActionLoading={bulkActionLoading}
              />
            </div>
          )}

          {dashboardView === "overview" && entries.length > 0 && (
            <PayrollFiltersCard
              searchTerm={searchTerm}
              setSearchTerm={setSearchTerm}
              statusFilter={statusFilter}
              setStatusFilter={setStatusFilter}
              departmentFilter={departmentFilter}
              setDepartmentFilter={setDepartmentFilter}
              departments={departments}
              filteredEntries={filteredEntries}
              entries={entries}
              selectedEntries={selectedEntries}
              setSelectedEntries={setSelectedEntries}
              bulkActionLoading={bulkActionLoading}
              handleBulkExport={handleBulkExport}
              handleExportExcel={handleExportExcel}
              handleBulkPDFGeneration={handleBulkPDFGeneration}
              handleBulkApprove={handleBulkApprove}
            />
          )}

          {dashboardView === "overview" && (
            <PayrollEntriesCard
              loading={loading}
              entries={entries}
              filteredEntries={filteredEntries}
              showAllEntries={showAllEntries}
              setShowAllEntries={setShowAllEntries}
              selectedEntries={selectedEntries}
              handleSelectAll={handleSelectAll}
              handleSelectEntry={handleSelectEntry}
              formatCurrency={formatCurrency}
              handleShowEntryDetails={handleShowEntryDetails}
              handleGeneratePDF={handleGeneratePDF}
              handleBulkExport={handleBulkExport}
              handleBulkApprove={handleBulkApprove}
              handleBulkReject={handleBulkReject}
              setSelectedEntries={setSelectedEntries}
              handleCalculatePeriod={handleCalculatePeriod}
              calculating={calculating}
              periodClosed={periodClosed}
              setSearchTerm={setSearchTerm}
              setStatusFilter={setStatusFilter}
              setDepartmentFilter={setDepartmentFilter}
            />
          )}
        </>
      )}

      <PayrollEntryDetails
        entry={selectedEntryForDetails}
        isOpen={isDetailsModalOpen}
        onClose={handleCloseEntryDetails}
        formatCurrency={formatCurrency}
      />

      <PayslipPDFModal
        isOpen={isPdfModalOpen}
        onClose={handleClosePdfModal}
        entry={selectedEntryForPdf}
        companyInfo={companyInfo}
        formatCurrency={formatCurrency}
      />
    </div>
  );
};

export default PayrollCalculations;
